// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  User,
  Users,
  Newspaper,
  TrendingUp,
  Shield,
  GraduationCap,
  School,
  Search,
  Video
} from 'lucide-react';

interface DashboardCard {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  color: string;
}

const dashboards: DashboardCard[] = [
  {
    title: 'Athlete Dashboard',
    description: 'View athlete profiles, recruiting progress, and performance metrics',
    icon: User,
    route: '/athlete-dashboard',
    color: 'text-blue-600'
  },
  {
    title: 'Parent Dashboard',
    description: 'Access parent portal for managing athlete accounts and communications',
    icon: Users,
    route: '/parent-dashboard',
    color: 'text-green-600'
  },
  {
    title: 'Press Dashboard',
    description: 'Manage press content, articles, and media distribution',
    icon: Newspaper,
    route: '/press-dashboard',
    color: 'text-purple-600'
  },
  {
    title: 'Sales Dashboard',
    description: 'Track sales pipeline, manage prospects, and analyze performance',
    icon: TrendingUp,
    route: '/sales-dashboard',
    color: 'text-orange-600'
  },
  {
    title: 'Club Coach Dashboard',
    description: 'Manage club teams, events, and athlete development programs',
    icon: Shield,
    route: '/club-coach-dashboard',
    color: 'text-red-600'
  },
  {
    title: 'High School Coach Dashboard',
    description: 'Oversee high school teams, schedules, and recruitment activities',
    icon: GraduationCap,
    route: '/hs-coach-dashboard',
    color: 'text-indigo-600'
  },
  {
    title: 'College Coach Dashboard',
    description: 'Recruit athletes, manage team needs, and track prospects',
    icon: School,
    route: '/college-coach-dashboard',
    color: 'text-cyan-600'
  },
  {
    title: 'Scout Dashboard',
    description: 'Evaluate athletes, maintain scouting reports, and track talent',
    icon: Search,
    route: '/scout-dashboard',
    color: 'text-yellow-600'
  },
  {
    title: 'Vendor Press Dashboard',
    description: 'Manage vendor press content and media partnerships',
    icon: Newspaper,
    route: '/vendor-press-dashboard',
    color: 'text-pink-600'
  },
  {
    title: 'Vendor Video Dashboard',
    description: 'Handle video production, packages, and client orders',
    icon: Video,
    route: '/vendor-video-dashboard',
    color: 'text-emerald-600'
  }
];

const AdminDashboardHub: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-3xl font-heading font-bold text-foreground">
          Admin Dashboard Hub
        </h1>
        <p className="text-muted-foreground mt-2">
          Access and manage all user dashboards from this central hub
        </p>
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground mt-3">
          <Link to="/admin-dashboard" className="hover:text-foreground transition-colors">
            Admin
          </Link>
          <span>/</span>
          <span className="text-foreground">User Dashboards</span>
        </nav>
      </div>

      {/* Dashboard Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {dashboards.map((dashboard) => (
          <Card 
            key={dashboard.title}
            className="group hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-border bg-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <dashboard.icon className={`h-8 w-8 ${dashboard.color}`} />
                <div className="h-2 w-2 rounded-full bg-green-500 opacity-75" />
              </div>
              <CardTitle className="text-lg font-semibold text-card-foreground line-clamp-2 min-h-[3.5rem]">
                {dashboard.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3 min-h-[4rem]">
                {dashboard.description}
              </p>
              <Link to={dashboard.route}>
                <Button 
                  className="w-full min-h-[44px] transition-colors"
                  variant="outline"
                >
                  Access Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Info */}
      <div className="bg-muted/50 rounded-lg p-6 border border-border">
        <h3 className="font-semibold text-foreground mb-2">Dashboard Access Information</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• All dashboards maintain their original functionality and permissions</li>
          <li>• Admin access allows view-only mode for most user-specific content</li>
          <li>• Some dashboards may require specific role assignments for full access</li>
          <li>• Dashboard availability depends on system configuration and feature flags</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboardHub;